// Drop-in wrapper around `fetch` that retries on transient failures.
//
// Why: a single `await fetch(...)` rejects with "Failed to fetch" on any
// pre-response-body network failure (DNS, TLS handshake, dropped packet
// during connection setup, WebView radio glitch). On spotty mobile data
// these are common — one shot, no retry, surface fail. This wraps the
// call in a small backoff loop so transient hiccups self-heal.
//
// Retry policy:
//   - Network/CORS errors (fetch throws TypeError) → retry.
//   - HTTP 408 / 425 / 429 / 500 / 502 / 503 / 504 → retry.
//   - Everything else (incl. 4xx user/auth errors, 2xx success) → return as-is.
//
// Backoff: 500ms × 2.5^attempt, with ±20% jitter to avoid synchronized
// retries hammering the backend after an outage. Default 3 retries
// (so up to 4 total attempts); total worst-case wait before the final
// throw is ~5.4s.
//
// AbortSignal in `options.signal` is honored — if the signal aborts
// mid-backoff, we throw the same AbortError fetch would have thrown
// without retrying further.

const RETRYABLE_HTTP_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

function isRetryableError(err) {
  if (!err) return false;
  // AbortError → caller cancelled, don't retry.
  if (err.name === 'AbortError') return false;
  // TypeError is what fetch throws for network failures, CORS, DNS, etc.
  // Be generous: anything that isn't an AbortError is a transient candidate.
  return true;
}

function backoffMs(attempt, baseDelayMs, factor) {
  const base = baseDelayMs * Math.pow(factor, attempt);
  // ±20% jitter.
  const jitter = base * 0.2 * (Math.random() * 2 - 1);
  return Math.max(0, Math.round(base + jitter));
}

function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal && signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const t = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(t);
        reject(new DOMException('Aborted', 'AbortError'));
      }, { once: true });
    }
  });
}

export async function fetchWithRetry(url, options = {}, retryOptions = {}) {
  const {
    retries = 3,
    baseDelayMs = 500,
    factor = 2.5,
  } = retryOptions;
  const signal = options.signal;

  let lastError = null;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, options);
      if (!RETRYABLE_HTTP_STATUSES.has(res.status) || attempt === retries) {
        return res;
      }
      // Retryable HTTP error: drain so the connection can be reused, then sleep.
      try { await res.text(); } catch (_) { /* best effort */ }
    } catch (err) {
      lastError = err;
      if (!isRetryableError(err) || attempt === retries) {
        throw err;
      }
    }
    await sleep(backoffMs(attempt, baseDelayMs, factor), signal);
  }
  // Unreachable — the loop either returns a response or throws — but TypeScript
  // / linters appreciate the safety net.
  if (lastError) throw lastError;
  throw new Error('fetchWithRetry: exhausted retries with no result');
}

export default fetchWithRetry;
